import type { EpgDateEx } from '~/entities/epg/dates'

import { createEvent, createStore, sample } from 'effector'
import { model as router } from '~/core/router'
import {
  epgDatesModel,
  fromString,
  isEpgDateString,
  isSameDate,
  isToday,
  toString,
} from '~/entities/epg/dates'
import { lookbehind } from '~/shared/effector'
import { QUERY_PARAM } from './index.h'

export const autoSetActive = createEvent<{ query: URLSearchParams }>()
export const setActive = createEvent<EpgDateEx | null>()
export const $active = createStore<EpgDateEx | null>(null)
  .on(setActive, (current, date) => {
    return current == null || date != null ? date : current
  })
  .on(autoSetActive, (_, { query }) => {
    const date = query.get(QUERY_PARAM)
    if (date == null || !isEpgDateString(date)) return null
    return fromString(date)
  })

//
// persist active date in query string
//

export const pushActiveToQuery = createEvent<EpgDateEx | null>()

lookbehind({
  source: $active,
  clear: autoSetActive,
  within: 0,
  target: pushActiveToQuery,
})

sample({
  clock: pushActiveToQuery,
  fn: (date) => ({
    [QUERY_PARAM]: date == null || isToday(date) ? null : toString(date),
  }),
  target: router.navigatePushQuery,
})

//
//
//

// initially selected by url date
export const $initial = createStore<EpgDateEx | null>(null)

// select first date if there is no active date yet
sample({
  clock: autoSetActive,
  source: {
    active: $active,
    week: epgDatesModel.$week,
  },
  filter: ({ active, week }) => active == null && week.length > 0,
  fn: ({ week }) => week[0],
  target: setActive,
})

// set initial date from query string
sample({
  clock: autoSetActive,
  source: {
    active: $active,
    initial: $initial,
    week: epgDatesModel.$week,
  },
  filter: ({ active, initial, week }) =>
    active != null && initial == null && week.find(isSameDate(active)) == null,
  fn: ({ active }) => active,
  target: $initial,
})

// set today date as active after day change
sample({
  clock: epgDatesModel.$today,
  source: {
    active: $active,
    week: epgDatesModel.$week,
  },
  // if today was updated, and active day disappeared from the week
  filter: ({ active, week }) =>
    active != null && week.find(isSameDate(active)) == null,
  // -> select first day from the week (new today)
  fn: ({ week }) => week[0],
  target: setActive,
})
